import React, { useState, useEffect, useCallback } from 'react';

const TOTAL_M2 = 95000.53;
const M2_TO_FT2 = 10.7639;

// Block definitions matching the new masterplan layout
const BLOCK_ORDER = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const DEFAULT_LOTS = [
  { id: 'A08', m2: 1216.47, block: 'A', status: 'sold' },
  { id: 'A28', m2: 1290.51, block: 'A', status: 'available' },
  { id: 'A38', m2: 1325.37, block: 'A', status: 'available' },
  { id: 'A48', m2: 1360.72, block: 'A', status: 'available' },
  { id: 'B08', m2: 1423.30, block: 'B', status: 'available' },
  { id: 'B18', m2: 1318.32, block: 'B', status: 'available' },
  { id: 'B28', m2: 1318.32, block: 'B', status: 'available' },
  { id: 'B38', m2: 1318.32, block: 'B', status: 'available' },
  { id: 'B48', m2: 1318.32, block: 'B', status: 'available' },
  { id: 'B58', m2: 1318.32, block: 'B', status: 'available' },
  { id: 'C08', m2: 1021.38, block: 'C', status: 'available' },
  { id: 'C18', m2: 1190.66, block: 'C', status: 'available' },
  { id: 'C28', m2: 1190.66, block: 'C', status: 'available' },
  { id: 'C38', m2: 1190.66, block: 'C', status: 'available' },
  { id: 'C48', m2: 1190.66, block: 'C', status: 'available' },
  { id: 'C58', m2: 1190.66, block: 'C', status: 'available' },
  { id: 'D08', m2: 1304.59, block: 'D', status: 'reserved' },
  { id: 'D18', m2: 1190.66, block: 'D', status: 'reserved' },
  { id: 'D28', m2: 1190.66, block: 'D', status: 'reserved' },
  { id: 'D38', m2: 1190.66, block: 'D', status: 'reserved' },
  { id: 'D48', m2: 1190.66, block: 'D', status: 'reserved' },
  { id: 'D58', m2: 1190.66, block: 'D', status: 'reserved' },
  { id: 'E08', m2: 1238.95, block: 'E', status: 'reserved' },
  { id: 'E18', m2: 1462.62, block: 'E', status: 'reserved' },
  { id: 'E28', m2: 1829.37, block: 'E', status: 'reserved' },
  { id: 'E38', m2: 1482.45, block: 'E', status: 'reserved' },
  { id: 'E48', m2: 1141.89, block: 'E', status: 'reserved' },
  { id: 'E58', m2: 1141.89, block: 'E', status: 'reserved' },
  { id: 'E68', m2: 1141.89, block: 'E', status: 'reserved' },
  { id: 'E78', m2: 1141.89, block: 'E', status: 'reserved' },
  { id: 'E88', m2: 1141.89, block: 'E', status: 'reserved' },
  { id: 'F08', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F18', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F28', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F38', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F48', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F58', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F68', m2: 955.72, block: 'F', status: 'available' },
  { id: 'F78', m2: 955.72, block: 'F', status: 'available' },
  { id: 'G08', m2: 1513.41, block: 'G', status: 'available' },
  { id: 'G18', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G28', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G38', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G48', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G58', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G68', m2: 955.72, block: 'G', status: 'available' },
  { id: 'G78', m2: 955.72, block: 'G', status: 'available' },
  { id: 'H08', m2: 1135.68, block: 'H', status: 'available' },
  { id: 'H18', m2: 858.25, block: 'H', status: 'available' },
  { id: 'H28', m2: 871.26, block: 'H', status: 'available' },
  { id: 'H38', m2: 1070.09, block: 'H', status: 'available' },
  { id: 'H48', m2: 915.96, block: 'H', status: 'available' },
];

const STATUS_BG = {
  sold: 'bg-gray-400 opacity-60',
  reserved: 'bg-amber-100 border-amber-300',
  available: 'bg-white hover:bg-green-50 cursor-pointer',
};

const Masterplan = ({ t, language, onSelectLot }) => {
  const [lots, setLots] = useState(DEFAULT_LOTS);

  const fetchLotStatus = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lots`);
      if (response.ok) {
        const data = await response.json();
        if (data.lots) {
          setLots(prev => prev.map(lot => {
            const apiLot = data.lots.find(l => l.id === lot.id);
            return apiLot ? { ...lot, status: apiLot.status } : lot;
          }));
        }
      }
    } catch {
      // fallback to defaults
    }
  }, []);

  useEffect(() => { fetchLotStatus(); }, [fetchLotStatus]);

  const fmt = (num) => num.toLocaleString(
    language === 'zh' ? 'zh-CN' : language === 'es' ? 'es-MX' : 'en-US',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  );

  const getArea = (m2) => language === 'en'
    ? `${fmt(m2 * M2_TO_FT2)} ft\u00b2`
    : `${fmt(m2)} m\u00b2`;

  const getTotalArea = () => language === 'en'
    ? `${fmt(TOTAL_M2 * M2_TO_FT2)} ft\u00b2`
    : `${fmt(TOTAL_M2)} m\u00b2`;

  const statusLabel = (s) => ({
    available: { en: 'Available', es: 'Disponible', zh: '\u53ef\u7528' },
    sold: { en: 'Sold', es: 'Vendido', zh: '\u5df2\u552e' },
    reserved: { en: 'Reserved', es: 'Reservado', zh: '\u5df2\u9884\u8ba2' },
  }[s]?.[language] || s);

  const handleClick = (lot) => {
    if (lot.status === 'available' && onSelectLot) onSelectLot(lot);
  };

  const blockLots = (block) => lots.filter(l => l.block === block);

  const avail = lots.filter(l => l.status === 'available').length;
  const reserved = lots.filter(l => l.status === 'reserved').length;
  const sold = lots.filter(l => l.status === 'sold').length;

  const LotCell = ({ lot }) => (
    <div
      onClick={() => handleClick(lot)}
      data-testid={`lot-cell-${lot.id}`}
      className={`relative border overflow-hidden transition-all p-1.5 sm:p-2 ${STATUS_BG[lot.status]} ${
        lot.status === 'available' ? 'hover:shadow-md hover:border-green-400' : ''
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[7px] sm:text-[8px] font-bold flex-shrink-0 ${
          lot.status === 'sold' ? 'bg-gray-500 text-white' : 'bg-gray-900 text-white'
        }`}>
          {lot.id}
        </span>
        <span className={`text-[7px] sm:text-[8px] font-medium leading-tight ${
          lot.status === 'sold' ? 'text-gray-500 line-through' : 'text-gray-600'
        }`}>
          {getArea(lot.m2)}
        </span>
      </div>
      {lot.status !== 'available' && (
        <div className={`absolute bottom-0.5 right-0.5 text-[5px] sm:text-[6px] px-0.5 sm:px-1 py-0.5 rounded ${
          lot.status === 'sold' ? 'bg-gray-500 text-white' : 'bg-amber-500 text-white'
        }`}>
          {statusLabel(lot.status)}
        </div>
      )}
    </div>
  );

  const BlockColumn = ({ block, width }) => (
    <div className={width}>
      <div className="text-[9px] sm:text-[10px] font-bold text-gray-500 text-center mb-1 uppercase tracking-wider">
        {block}
      </div>
      <div className="flex flex-col gap-1">
        {blockLots(block).map(lot => <LotCell key={lot.id} lot={lot} />)}
      </div>
    </div>
  );

  const Road = () => <div className="w-1.5 sm:w-2 bg-gray-300/50 self-stretch" />;

  return (
    <div className="p-4 sm:p-5 lg:p-6 border border-gray-200 relative overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50">
      {/* Header */}
      <div className="mb-4 sm:mb-6 relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <img src="/images/logos/recurso_63.svg" alt="Hexaparks" className="h-5 sm:h-6 w-auto" style={{ filter: 'brightness(0)' }} />
          <div className="flex flex-col items-center leading-none">
            <img src="/images/logos/hexaparks_wordmark.svg" alt="Hexaparks" className="h-2.5 sm:h-3 w-auto" />
            <span className="text-[5px] sm:text-[6px] text-gray-600 tracking-[0.15em] uppercase mt-[1px]">{t.logo?.slogan}</span>
          </div>
        </div>
        <p className="text-base sm:text-lg text-gray-700 italic mb-3 font-light">{t.masterplan.subtitle}</p>
        <div className="inline-flex items-center bg-gray-900 text-white px-3 py-2 gap-2 rounded">
          <span className="font-bold text-sm sm:text-base">{getTotalArea()}</span>
          <span className="text-gray-400 text-xs border-l border-gray-600 pl-2">{t.masterplan.totalArea}</span>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 sm:gap-4 mt-3 text-[10px] sm:text-xs">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-white border border-gray-300 rounded-sm" />
            <span className="text-gray-600">{statusLabel('available')} ({avail})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-amber-100 border border-amber-300 rounded-sm" />
            <span className="text-gray-600">{statusLabel('reserved')} ({reserved})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 bg-gray-400 rounded-sm" />
            <span className="text-gray-600">{statusLabel('sold')} ({sold})</span>
          </div>
        </div>
      </div>

      {/* Static Site Map Image */}
      <div className="mb-6">
        <img src="/images/site_map.png" alt="Hexaparks Site Map" className="w-full h-auto rounded border border-gray-200 shadow-sm" />
      </div>

      {/* Interactive Block Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[700px] relative">
          <div className="relative p-3 sm:p-4 bg-gradient-to-br from-gray-200 to-gray-100 rounded">
            {/* Grid: H G F E D C B A (left to right matching the map) */}
            <div className="flex gap-0 items-start">
              <BlockColumn block="H" width="w-[10%]" />
              <Road />
              <BlockColumn block="G" width="w-[11%]" />
              <BlockColumn block="F" width="w-[11%]" />
              <Road />
              <BlockColumn block="E" width="w-[13%]" />
              <BlockColumn block="D" width="w-[11%]" />
              <Road />
              <BlockColumn block="C" width="w-[11%]" />
              <BlockColumn block="B" width="w-[11%]" />
              <Road />
              <BlockColumn block="A" width="w-[9%]" />
            </div>

            {/* Business Center label */}
            <div className="mt-2 flex justify-center">
              <div className="bg-gray-800 text-white text-[9px] sm:text-[11px] px-3 py-1.5 text-center font-semibold rounded">
                {t.masterplan.businessCenter}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-[10px] sm:text-xs text-gray-500 mt-3 italic">
        {t.masterplan.clickInstruction}
      </p>
    </div>
  );
};

export default Masterplan;
